import { addCalendarDays, toCalendarDate, toDate } from "@repo/utils/date";
import { calendarDateSchema } from "./dates";
import type { DocumentLifecycleView } from "./document-review";
import {
  type DocumentValidityEntry,
  type DocumentValidityUnit,
  getDocumentValidityEntry,
  hasComputableValidity,
  normalizeDocName,
} from "./document-validity";

/**
 * The fixed driver documents, shared by the admin review screen and the mobile forms.
 * A document groups the files uploaded for it (e.g. licence front and back). Which date a
 * document needs is not declared here: it is derived from `DOCUMENT_VALIDITY`.
 */
export type DriverDocumentFileKind = "image" | "file";

export interface DriverDocumentFile {
  /** Stored document name, also the driver form field for legacy paths. */
  name: string;
  /** Label of the file within its document ("Recto"); falls back to the document label. */
  side?: string;
  kind: DriverDocumentFileKind;
  /** Upload folder key used by the mobile app. */
  uploadKey: string;
  /** Legacy stored names that resolve to this file. */
  aliases?: readonly string[];
}

export interface DriverDocumentDefinition {
  id: string;
  label: string;
  files: readonly DriverDocumentFile[];
}

export const DRIVER_DOCUMENTS = [
  {
    id: "identity",
    label: "Carte d'identité",
    files: [
      { name: "idPath", side: "Recto", kind: "image", uploadKey: "idPath" },
      { name: "idPath2", side: "Verso", kind: "image", uploadKey: "idPath2" },
    ],
  },
  {
    id: "drivingLicense",
    label: "Permis de conduire",
    files: [
      { name: "drivingLicensePath", side: "Recto", kind: "image", uploadKey: "drivingLicensePath" },
      {
        name: "drivingLicensePath2",
        side: "Verso",
        kind: "image",
        uploadKey: "drivingLicensePath2",
      },
    ],
  },
  {
    id: "permitBalance",
    label: "Solde de permis",
    files: [{ name: "permit_solde_path", kind: "file", uploadKey: "permit_solde_path" }],
  },
  {
    id: "insurance",
    label: "Attestation d'assurance",
    files: [
      {
        name: "insurance_document_path",
        kind: "file",
        uploadKey: "insurance_document_path",
        aliases: ["insurance"],
      },
    ],
  },
  {
    id: "kbis",
    label: "KBIS",
    files: [{ name: "kbisPath", kind: "file", uploadKey: "kbis" }],
  },
  {
    id: "vigilance",
    label: "Attestation de vigilance",
    files: [
      {
        name: "vigilance_certificate_path",
        kind: "file",
        uploadKey: "vigilance_certificate",
        aliases: ["vigilance"],
      },
    ],
  },
  {
    id: "rib",
    label: "RIB",
    files: [{ name: "ribPath", kind: "file", uploadKey: "ribPath" }],
  },
] as const satisfies readonly DriverDocumentDefinition[];

export type DriverDocumentId = (typeof DRIVER_DOCUMENTS)[number]["id"];

export function driverDocumentFileLabel(
  document: DriverDocumentDefinition,
  file: DriverDocumentFile
): string {
  return file.side ? `${document.label} (${file.side.toLowerCase()})` : document.label;
}

/** Every uploadable file, in display order, with its document and full label. */
export const DRIVER_DOCUMENT_FILES = DRIVER_DOCUMENTS.flatMap(
  (document: DriverDocumentDefinition) =>
    document.files.map((file) => ({
      ...file,
      documentId: document.id,
      label: driverDocumentFileLabel(document, file),
    }))
);

const fileByName = new Map(
  DRIVER_DOCUMENT_FILES.flatMap((file) =>
    [file.name, file.uploadKey, ...(file.aliases ?? [])].map(
      (name) => [normalizeDocName(name), file] as const
    )
  )
);

/** Resolves any stored name, upload key or legacy alias to its file. */
export function findDriverDocumentFile(name: string) {
  return fileByName.get(normalizeDocName(name));
}

export function findDriverDocument(id: string): DriverDocumentDefinition | undefined {
  return DRIVER_DOCUMENTS.find((document) => document.id === id);
}

export interface DocumentDateField {
  /** Driver form field where the driver proposes the date. */
  field: string;
  kind: "issuedAt" | "expiryDate";
  label: string;
  rule: DocumentValidityEntry & { validity: number; unit: DocumentValidityUnit };
}

/** The date a document needs, derived from its validity rule; null when non-expiring. */
export function getDocumentDateField(name: string): DocumentDateField | null {
  const rule = getDocumentValidityEntry(name);
  if (!hasComputableValidity(rule) || !rule.driverField || !rule.dateType) return null;
  return {
    field: rule.driverField,
    kind: rule.dateType,
    label: rule.dateType === "issuedAt" ? "Date d'émission" : "Date d'expiration",
    rule,
  };
}

/** A document's files share one date; the first file carries the rule. */
export function getDriverDocumentDateField(
  document: DriverDocumentDefinition
): DocumentDateField | null {
  const [first] = document.files;
  return first ? getDocumentDateField(first.name) : null;
}

const unitLabelsFr: Record<DocumentValidityUnit, string> = {
  DAY: "jour(s)",
  MONTH: "mois",
  YEAR: "an(s)",
};

export function documentDateRuleLabelFr(dateField: DocumentDateField): string {
  const { rule } = dateField;
  return dateField.kind === "issuedAt"
    ? `Valable ${rule.validity} ${unitLabelsFr[rule.unit]} après la date d'émission.`
    : "La date d'expiration inscrite sur le document est utilisée.";
}

/** Form value for a date-only input (YYYY-MM-DD). */
export const documentDateValueSchema = calendarDateSchema;

export function toDocumentDateValue(value: Date | string | null | undefined): string {
  return toDate(value) ? toCalendarDate(value as Date | string) : "";
}

/** Only pending or current versions of expiring documents take reviewed dates. */
export function canReviewDocumentDates(
  view: Pick<DocumentLifecycleView, "name" | "versionStatus">
): boolean {
  return ["PENDING", "APPROVED"].includes(view.versionStatus) && !!getDocumentDateField(view.name);
}

/** The admin's last reviewed date wins over the driver's suggestion. */
export function initialDocumentReviewDate(
  view: Pick<DocumentLifecycleView, "review" | "suggestedDate">
): string {
  return view.review?.source === "ADMIN" ? view.review.date : (view.suggestedDate ?? "");
}

/** Approval of an expiring document must carry an admin-reviewed date. */
export function needsDocumentDateReview(
  view: Pick<DocumentLifecycleView, "name" | "versionStatus" | "review">
): boolean {
  return canReviewDocumentDates(view) && view.review?.source !== "ADMIN";
}

/** Stored expiries are the start of the day after the last valid day (see expiryFromDocumentDate). */
export function documentLastValidDay(expiresAt: Date | string | null | undefined): string {
  return toDate(expiresAt) ? addCalendarDays(toCalendarDate(expiresAt as Date | string), -1) : "";
}

/** Other files of the same document; they share its date (e.g. licence front and back). */
export function getSameDocumentFileNames(name: string): string[] {
  const file = findDriverDocumentFile(name);
  if (!file) return [];
  const document = findDriverDocument(file.documentId);
  return document?.files.map((entry) => entry.name).filter((entry) => entry !== file.name) ?? [];
}
